import React from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { Badge } from '../ui/badge';
import { cn } from '../lib/utils';
import { unitTopicBadgeClass } from './unitCardTheme';
import type { LearningObjectiveDto } from './InProgressUnitsPanel/types';
import { UnitHeaderTopicsPlainLabel, type UnitHeaderTone } from './UnitHeaderTopicLabels';

function normalizeTopicKey(name: string): string {
    return name.trim().toLowerCase();
}

type TopicRow = {
    key: string;
    label: string;
    group: LearningObjectiveDto | null;
};

/** Join header topic names to LO groups; orphan groups become extra chips. */
export function buildTopicObjectiveRows(
    topics: string[] | undefined,
    learningObjectives: LearningObjectiveDto[] | undefined
): TopicRow[] {
    const groups = learningObjectives ?? [];
    const byKey = new Map<string, LearningObjectiveDto>();
    for (const group of groups) {
        const name = (group.e251_TopicName || '').trim();
        if (!name) continue;
        byKey.set(normalizeTopicKey(name), group);
    }

    const used = new Set<string>();
    const rows: TopicRow[] = [];
    for (const raw of topics ?? []) {
        const label = String(raw).trim();
        if (!label) continue;
        const key = normalizeTopicKey(label);
        const group = byKey.get(key) ?? null;
        if (group) used.add(key);
        rows.push({ key: `${key}-${rows.length}`, label, group });
    }
    for (const [key, group] of byKey) {
        if (used.has(key)) continue;
        const label = (group.e251_TopicName || '').trim() || 'Unknown Topic';
        rows.push({ key: `orphan-${key}`, label, group });
    }
    return rows;
}

function objectiveLists(group: LearningObjectiveDto) {
    const completed = group.e251_CompletedObjectives || [];
    const incomplete = group.e251_IncompleteObjectives || [];
    return {
        completed,
        incomplete,
        all: [...completed, ...incomplete],
        total: completed.length + incomplete.length,
        completedCount: completed.length,
    };
}

interface UnitHeaderTopicsWithObjectivesProps {
    topics: string[] | undefined;
    learningObjectives?: LearningObjectiveDto[];
    /** Show completion fractions and checkmarks (finished / completed units). */
    showStatus?: boolean;
    tone?: UnitHeaderTone;
}

/** Topic chips that expand learning objectives inline under the chip row. */
export function UnitHeaderTopicsWithObjectives({
    topics,
    learningObjectives,
    showStatus = false,
    tone: _tone = 'default',
}: UnitHeaderTopicsWithObjectivesProps) {
    const rows = React.useMemo(
        () => buildTopicObjectiveRows(topics, learningObjectives),
        [topics, learningObjectives]
    );
    const [expandedKeys, setExpandedKeys] = React.useState<Set<string>>(new Set());

    const toggleTopic = (rowKey: string, e: React.MouseEvent) => {
        e.stopPropagation();
        e.preventDefault();
        setExpandedKeys(prev => {
            const next = new Set(prev);
            if (next.has(rowKey)) {
                next.delete(rowKey);
            } else {
                next.add(rowKey);
            }
            return next;
        });
    };

    if (rows.length === 0) {
        return (
            <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-gray-800">
                <UnitHeaderTopicsPlainLabel />
                <Badge variant="outline" className={cn(unitTopicBadgeClass, 'opacity-80')}>
                    Untitled Unit
                </Badge>
            </div>
        );
    }

    const expandedRows = rows.filter(r => r.group && expandedKeys.has(r.key));

    return (
        <div className="flex min-w-0 flex-col gap-1.5">
            <div className="flex flex-wrap items-center gap-x-2 gap-y-1.5">
                <UnitHeaderTopicsPlainLabel />
                <div className="flex flex-wrap items-center gap-1.5">
                {rows.map(row => {
                    const hasObjectives = !!row.group && objectiveLists(row.group).total > 0;
                    if (!hasObjectives || !row.group) {
                        return (
                            <Badge
                                key={row.key}
                                variant="outline"
                                className={unitTopicBadgeClass}
                            >
                                {row.label}
                            </Badge>
                        );
                    }

                    const isExpanded = expandedKeys.has(row.key);

                    return (
                        <button
                            key={row.key}
                            type="button"
                            aria-expanded={isExpanded}
                            onClick={e => toggleTopic(row.key, e)}
                            onMouseDown={e => e.stopPropagation()}
                            className={cn(
                                unitTopicBadgeClass,
                                'inline-flex max-w-full cursor-pointer items-start gap-1 hover:bg-silver-tree-dark focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blumine focus-visible:ring-offset-1'
                            )}
                        >
                            {isExpanded ? (
                                <ChevronDown size={12} className="mt-0.5 shrink-0 opacity-90" aria-hidden />
                            ) : (
                                <ChevronRight size={12} className="mt-0.5 shrink-0 opacity-90" aria-hidden />
                            )}
                            <span className="min-w-0 break-words">{row.label}</span>
                        </button>
                    );
                })}
                </div>
            </div>

            {expandedRows.length > 0 && (
                <div
                    className="rounded-md border border-gray-200/80 bg-white/80 px-2.5 py-2"
                    onClick={e => e.stopPropagation()}
                    onMouseDown={e => e.stopPropagation()}
                >
                    <div className="mb-1.5 text-xs font-semibold text-gray-800">
                        {showStatus ? 'Learning Objectives Status' : 'Learning Objectives'}
                    </div>
                    <div className="space-y-2">
                        {expandedRows.map(row => {
                            const group = row.group!;
                            const lists = objectiveLists(group);
                            return (
                                <div key={`panel-${row.key}`}>
                                    {(expandedRows.length > 1 || showStatus) && (
                                        <div className="mb-1 flex items-center justify-between gap-2">
                                            <div className="text-[11px] font-medium text-gray-600">
                                                {row.label}
                                            </div>
                                            {showStatus && (
                                                <span className="shrink-0 tabular-nums text-[11px] text-gray-500">
                                                    {lists.completedCount}/{lists.total}
                                                </span>
                                            )}
                                        </div>
                                    )}
                                    <ul className="space-y-0.5">
                                        {showStatus ? (
                                            <>
                                                {lists.completed.map((objective, i) => (
                                                    <li
                                                        key={`c-${i}`}
                                                        className="flex gap-1.5 text-xs leading-snug text-gray-700"
                                                    >
                                                        <span className="shrink-0 text-confirm-action">
                                                            ✓
                                                        </span>
                                                        <span>{objective}</span>
                                                    </li>
                                                ))}
                                                {lists.incomplete.map((objective, i) => (
                                                    <li
                                                        key={`i-${i}`}
                                                        className="flex gap-1.5 text-xs leading-snug text-gray-400"
                                                    >
                                                        <span className="shrink-0">○</span>
                                                        <span>{objective}</span>
                                                    </li>
                                                ))}
                                            </>
                                        ) : (
                                            lists.all.map((objective, i) => (
                                                <li
                                                    key={`o-${i}`}
                                                    className="flex gap-1.5 text-xs leading-snug text-gray-700"
                                                >
                                                    <span className="shrink-0 text-gray-400">•</span>
                                                    <span>{objective}</span>
                                                </li>
                                            ))
                                        )}
                                    </ul>
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}
        </div>
    );
}
